import React from "react";
import { X } from "lucide-react";
import { formatTransferFee } from "../../utils/currencyFormatter";

const formatPosition = (pos) => {
  if (!pos) return "-";
  return pos
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "accepted":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    case "pitch_sent":
    case "active":
      return "bg-yellow-100 text-yellow-800";
    case "expired":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const PitchDetailsModal = ({ isOpen, onClose, pitch }) => {
  if (!isOpen || !pitch) return null;

  // The backend's verdict, read and not recomputed. The fee-based fallback
  // that used to sit here is gone: `free_agent` is always present in the
  // response, so the branch was dead — and it was a second, divergent copy of
  // a rule that now lives once (agent_pitch_logic.resolve_free_agent), where
  // it also knows about staff requests and free-agents-only requests.
  const isFreeAgent = pitch?.free_agent === true;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Pitch Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Free Agent banner — prominently surfaced at the top so the
            club / agent immediately understands no fee is required. */}
        {isFreeAgent && (
          <div className="mx-6 mt-6 flex items-center gap-3 p-3 rounded-lg bg-[#ECFDF5] border border-[#10B981]">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#10B981] text-white text-sm font-bold">
              ★
            </span>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-[#047857]">
                Free Agent
              </span>
              <span className="text-xs text-[#065F46]">
                No transfer fee required for this player.
              </span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Player Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Player Information
            </h3>
            <div className="flex items-center gap-4 mb-4">
              {pitch.player?.image ? (
                <img
                  src={pitch.player.image}
                  alt={pitch.player.name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
              )}
              <div>
                <h4 className="text-xl font-semibold text-gray-900">
                  {pitch.player?.name || "Name not available"}
                </h4>
                <p className="text-gray-600">
                  {formatPosition(pitch.player?.position) ||
                    "Position not available"}
                </p>
                {pitch.player?.sportmonks_id && (
                  <p className="text-sm text-gray-500">
                    SportMonks ID: {pitch.player.sportmonks_id}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Pitch Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                    pitch.status
                  )}`}
                >
                  {pitch.status?.charAt(0).toUpperCase() +
                    pitch.status?.slice(1) || "N/A"}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Request Position
                </label>
                <p className="text-gray-900">
                  {formatPosition(pitch.tender_position) || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created At
                </label>
                <p className="text-gray-900">{formatDate(pitch.created_at)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expires At
                </label>
                <p className="text-gray-900">{formatDate(pitch.expires_at)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transfer Fee
                </label>
                {/* The verdict already decided this row above; what is left
                    here is only "how much". A pitch that is not a free agent
                    and carries no amount says NOTHING — `formatTransferFee`
                    returns "-" for an absent value, and a dash in a fee field
                    reads as "no fee", i.e. the same free-transfer claim the
                    verdict did not make.

                    A zero is "no amount" too, as it is everywhere else on the
                    pitch surfaces (viewpitches/pitchFields.transferFeeOf): the
                    decimal arrives as the STRING "0.00", which is truthy, so a
                    bare truthiness test would print "€0" — a price nobody
                    asked. */}
                {isFreeAgent ? (
                  <p className="text-[#047857] font-semibold">
                    Free Agent — no fee required
                  </p>
                ) : Number(pitch.transfer_fee) > 0 ? (
                  <p className="text-gray-900">
                    {formatTransferFee(pitch.transfer_fee, pitch.transfer_fee_currency)}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time Left
                </label>
                <p className="text-gray-900">{pitch.time_left || "N/A"}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Is Boosted
                </label>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    pitch.is_boosted
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {pitch.is_boosted ? "Yes" : "No"}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Is Lite
                </label>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    pitch.is_lite
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {pitch.is_lite ? "Yes" : "No"}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Has Documents
                </label>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    pitch.has_documents
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {pitch.has_documents ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </div>

          {/* Pitch Content */}
          {pitch.content && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pitch Content
              </label>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-900 whitespace-pre-wrap">
                  {pitch.content}
                </p>
              </div>
            </div>
          )}

          {/* Applied By Information */}
          {pitch.applied_by_agent && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Applied By
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agent Name
                  </label>
                  <p className="text-gray-900">
                    {pitch.applied_by_agent.name || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Agency Information */}
          {pitch.agency && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Agency Information
              </h3>
              <div className="flex items-center gap-3">
                {pitch.agency.logo && (
                  <img
                    src={pitch.agency.logo}
                    alt={pitch.agency.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                )}
                <div>
                  <p className="font-medium text-gray-900">
                    {pitch.agency.name}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Documents */}
          {pitch.has_documents && pitch.document && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attached Documents
              </label>
              <a
                href={pitch.document}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
                View Document
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PitchDetailsModal;
